// web/src/registry/features/_part-5.ts —— FEATURES 的第 5/6 段（24 个元素：analytics / ai / telephony）
// 拆自 web/src/registry/features.ts（元素原样搬运、顺序不变）；由入口按顺序展开组合，故本片是**内部片**，不对外导出。
import type { Feature } from '../features';

export const FEATURES_PART_5: Feature[] = [

  {
    id: 'analytics.report.service',
    title: '服务与效率',
    domain: 'analytics',
    scope: 'domain',
    type: 'analytics',
    route: '/report/service',
    permission: 'analytics.report',
    nav: { section: 'business', group: 'analytics', order: 3 },
    priority: 'P1',
  },

  // ───────────────────────────── 平台能力 ─────────────────────────────

  // AI 能力
  {
    id: 'ai.providers',
    title: '供应商',
    domain: 'ai',
    scope: 'platform',
    type: 'resource',
    route: '/platform/ai/providers',
    legacyLocations: ['/aicenter'],
    permission: 'ai.providers',
    nav: { section: 'platform', group: 'ai', order: 1 },
    priority: 'P1',
  },
  {
    id: 'ai.models.asr',
    title: 'ASR 模型',
    domain: 'ai',
    scope: 'platform',
    type: 'platform-capability',
    route: '/platform/ai/models/asr',
    orphan: true,
    legacyLocations: ['/aicenter'],
    permission: 'ai.providers',
    priority: 'P2',
  },
  {
    id: 'ai.models.fileasr',
    title: '录音文件识别模型',
    domain: 'ai',
    scope: 'platform',
    type: 'platform-capability',
    route: '/platform/ai/models/file-asr',
    orphan: true,
    legacyLocations: ['/aicenter'],
    permission: 'ai.providers',
    priority: 'P2',
  },
  {
    id: 'ai.models.tts',
    title: 'TTS 模型',
    domain: 'ai',
    scope: 'platform',
    type: 'platform-capability',
    route: '/platform/ai/models/tts',
    orphan: true,
    legacyLocations: ['/aicenter'],
    permission: 'ai.providers',
    priority: 'P2',
  },
  {
    id: 'ai.models.llm',
    title: 'LLM 模型',
    domain: 'ai',
    scope: 'platform',
    type: 'platform-capability',
    route: '/platform/ai/models/llm',
    orphan: true,
    legacyLocations: ['/aicenter'],
    permission: 'ai.providers',
    priority: 'P2',
  },
  {
    id: 'ai.models.embedding',
    title: 'Embedding 模型',
    domain: 'ai',
    scope: 'platform',
    type: 'platform-capability',
    route: '/platform/ai/models/embedding',
    orphan: true,
    legacyLocations: ['/aicenter'],
    permission: 'ai.providers',
    priority: 'P2',
  },
  {
    id: 'ai.prompts',
    title: '提示词',
    domain: 'ai',
    scope: 'platform',
    type: 'platform-capability',
    route: '/platform/ai/prompts',
    legacyLocations: ['/aicenter/prompt-console'],
    permission: 'ai.prompts',
    nav: { section: 'platform', group: 'ai', order: 2 },
    priority: 'P2',
    issue: '提示词是否按业务域分权维护待确认（可能部分 DOMAIN）',
  },
  {
    id: 'ai.usage',
    title: '调用监控',
    domain: 'ai',
    scope: 'platform',
    type: 'analytics',
    route: '/platform/ai/usage',
    legacyLocations: ['/aicenter/llm-history'],
    permission: 'ai.usage',
    nav: { section: 'platform', group: 'ai', order: 3 },
    priority: 'P1',
    issue: '原 LLM 调用历史含“用量”与“审计”两义；用量归平台，留痕归 audit.ai',
  },
  {
    id: 'ai.cost',
    title: '运营与成本',
    domain: 'ai',
    scope: 'platform',
    type: 'config',
    route: '/platform/ai/cost',
    legacyLocations: ['/aicenter'],
    permission: 'ai.cost',
    nav: { section: 'platform', group: 'ai', order: 4 },
    priority: 'P1',
    issue: '运维成本原混入业务报表（/report AI 分析）',
  },
  {
    id: 'ai.studio',
    title: '智能调试台',
    domain: 'ai',
    scope: 'platform',
    type: 'resource',
    route: '/platform/ai/studio',
    legacyLocations: ['/aicenter/llm-studio'],
    permission: 'ai.studio',
    nav: { section: 'platform', group: 'ai', order: 6 },
    priority: 'P2',
    issue: 'LLM Studio 调试台（原 /aicenter/llm-studio）',
  },

  // 通信能力
  {
    id: 'telephony.gateways',
    title: 'SIP网关',
    domain: 'telephony',
    scope: 'platform',
    type: 'platform-capability',
    route: '/platform/telephony/gateways',
    legacyLocations: ['/system/sip-gateways'],
    permission: 'telephony.gateways',
    nav: { section: 'platform', group: 'telephony', order: 1 },
    priority: 'P0',
    issue: '平台能力埋在 /system',
  },
  {
    id: 'telephony.sip',
    title: 'SIP 参数',
    domain: 'telephony',
    scope: 'platform',
    type: 'platform-capability',
    route: '/platform/telephony/sip',
    legacyLocations: ['/system/sip-settings'],
    permission: 'telephony.sip',
    priority: 'P0',
    issue: '平台能力埋在 /system',
  },
  {
    id: 'telephony.trunks',
    title: '中继',
    domain: 'telephony',
    scope: 'platform',
    type: 'platform-capability',
    route: '/platform/telephony/trunks',
    legacyLocations: ['/system/trunk-accounts'],
    permission: 'telephony.trunks',
    nav: { section: 'platform', group: 'telephony', order: 2 },
    priority: 'P0',
    issue: '平台能力埋在 /system',
  },
  {
    id: 'telephony.audio',
    title: '放音资源管理',
    domain: 'telephony',
    scope: 'platform',
    type: 'resource',
    route: '/platform/telephony/audio',
    legacyLocations: ['/system/audio-files'],
    permission: 'telephony.audio',
    nav: { section: 'platform', group: 'telephony', order: 5 },
    priority: 'P1',
    issue: '被 IVR/外呼/提示音复用，原在 /system',
  },
  {
    id: 'telephony.settings',
    title: '通信设置',
    domain: 'telephony',
    scope: 'platform',
    type: 'config',
    route: '/platform/telephony/settings',
    // 设置 Landing：任一子配置权限即可见（当前仅 admin 持有），页内 Tab 按各自子码裁剪
    permission: ['telephony.settings', 'telephony.sip', 'telephony.capacity'],
    nav: { section: 'platform', group: 'telephony', order: 6 },
    priority: 'P1',
    issue: 'Settings Landing：聚合 SIP 参数/通信容量；含 reloadxml/sofia-restart 等破坏性操作，仅平台 admin + 二次确认',
  },
  {
    id: 'telephony.routing',
    title: '进线路由',
    domain: 'telephony',
    scope: 'platform',
    type: 'config',
    route: '/platform/telephony/routing',
    legacyLocations: ['/system/routes', '/inbound/routes', '/inbound/settings?tab=routes'],
    // 入口对「持有任一子配置权限」的角色可见（主管持 inbound.ivr → 只看到 IVR 菜单 Tab），
    // 页内 Tab 各自按子码裁剪（见 registry/navTabs.ts 与 pages/telephony/Routing.tsx）
    permission: ['inbound.routes', 'inbound.ivr'],
    nav: { section: 'platform', group: 'telephony', order: 4 },
    priority: 'P1',
    issue: '原为呼入域两个独立配置项（呼叫路由/IVR）；两者互为对方的字段（路由目的地选 IVR 菜单、菜单按键选队列/分机/子菜单），按「一次接线配置的两步」合并为一页两 Tab 并迁入通信能力',
  },
  {
    id: 'telephony.routing.ivr',
    title: 'IVR 菜单',
    domain: 'telephony',
    scope: 'platform',
    type: 'config',
    route: '/platform/telephony/routing/ivr',
    legacyLocations: ['/system/ivr-menus', '/inbound/ivr'],
    permission: 'inbound.ivr',
    priority: 'P1',
  },
  {
    id: 'telephony.sms',
    title: '短信记录',
    domain: 'telephony',
    scope: 'platform',
    type: 'record',
    route: '/platform/telephony/sms',
    legacyLocations: ['/inbound/sms'],
    permission: 'inbound.sms',
    nav: { section: 'platform', group: 'telephony', order: 7 },
    priority: 'P1',
    issue: '短信通道留痕（验证码/回访/催缴/外呼挂机短信，发送方跨 worker/media/api）：原按「验证码留痕」归入呼入域，实为多域复用的通知渠道记录，按归属迁入通信能力；权限码仍为 inbound.sms（权限码整体重构时一并改名）',
  },
  {
    id: 'telephony.capacity',
    title: '呼叫与媒体',
    domain: 'telephony',
    scope: 'platform',
    type: 'config',
    route: '/platform/telephony/capacity',
    legacyLocations: ['/system/sys-settings'],
    permission: 'telephony.capacity',
    priority: 'P1',
    issue: '原为系统设置「呼叫与媒体」页签（FS 域/媒体 WS/外呼与在线并发），按归属迁入通信能力域',
  },];
